<script setup lang="ts">
import { computed, onBeforeMount, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import FacetFilter from "@/components/ActivitySearchFilters/FacetFilter.vue";
import InteractieveKaartActivitySwapper from "@/views/InteractieveKaart/components/InteractieveKaartActivitySwapper.vue";
import CmsDataProvider, { CMS_DATA_ID } from "@/providers/cms.provider";
import { HiddenFilters } from "@/view-models/filters.model";
import { Facet, FacetGroup } from "@/models/search.model";
import { IActiviteitSearchParams } from "@/repositories/activiteit.repository";
import { ISportaanbiederSearchParams } from "@/repositories/sportaanbieder.repository";
import { usePlatformStore } from "@/stores/platform.store";
import kenmerkenRepository, { Kenmerk } from "@/repositories/kenmerk.repository";

export type ActivityType = "bewegen" | "beleven" | "spelen";
type PinType = "aanbieder" | "activiteit" | "speelplek" | "wandelroute" | "fietsroute";
const props = withDefaults(defineProps<{
    active?: ActivityType;
    pins: any[];
    resultCount: number;
    activityFacetGroups: FacetGroup[];
    clubFacetGroups: FacetGroup[];
}>(), {
    active: "bewegen",
    activityFacetGroups: () => [],
    clubFacetGroups: () => [],
});

const emits = defineEmits<{
    (e: "change-activity", activity: ActivityType): void;
    (e: "change-play-types", selectedTypes: string[]): void;
    (e: "change-activity-filters", filters: Record<string, string[] | number>): void;
    (e: "change-club-filters", filters: Record<string, string[]>): void;
}>();

const cmsData = CmsDataProvider.get<{
    activitiesSingular: string,
    activitiesPlural: string,
    clubsSingular: string,
    clubsPlural: string,
    hiddenFiltersActivities?: HiddenFilters;
    hiddenFiltersClubs?: HiddenFilters;
}>(CMS_DATA_ID.INTERACTIEVE_KAART);

const platformStore = usePlatformStore();

const selectedActivity = ref<ActivityType>(props.active);
const selectedPlayTypes = ref<string[]>([]);
const activityFacetGroups = computed<FacetGroup[]>(() => props.activityFacetGroups ?? []);
const clubFacetGroups = computed<FacetGroup[]>(() => props.clubFacetGroups ?? []);
const activiteitKenmerken = reactive<Record<string, Kenmerk[]>>({});
const clubKenmerken = reactive<Record<string, Kenmerk[]>>({});
const activityLastFacetChanged = ref<string | undefined>(undefined);
const clubLastFacetChanged = ref<string | undefined>(undefined);
const isSidebarCollapsed = ref(false);
const isMobile = ref(false);
const isSheetOpen = ref(false);
let mediaQueryList: MediaQueryList | null = null;

const activityFilterValues = reactive<Partial<Record<keyof IActiviteitSearchParams, string[]>>>({
    cultuuractiviteiten_facet: [],
    soortactiviteit_facet: [],
    sporten_facet: [],
    sporttypen_facet: [],
    plaatsen_facet: [],
    wijken_facet: [],
    wijkengemeenten_facet: [],
    betalingstype_facet: [],
    extrakosten_facet: [],
    doelgroep_facet: [],
    specifiekaanbod_facet: [],
    fysiekeinspanning_facet: [],
    golfstarttype_facet: [],
});
const selectedAge = ref<number | undefined>(undefined);

const clubFilterValues = reactive<Partial<Record<keyof ISportaanbiederSearchParams, string[]>>>({
    cultuuractiviteiten_facet: [],
    sporten_facet: [],
    plaatsen_facet: [],
    faciliteiten_facet: [],
    aanvullendefaciliteiten_facet: [],
    specifiekaanbod_facet: [],
    lesvormen_facet: [],
});

const hiddenFiltersActivities = computed<HiddenFilters>(() => {
    return cmsData.hiddenFiltersActivities
        ?? {};
});

const hiddenFiltersClubs = computed<HiddenFilters>(() => {
    return cmsData.hiddenFiltersClubs
        ?? {};
});

const playTypeDefinitions: Array<{ id: PinType; label: string; order: number }> = [
    { id: "aanbieder", label: cmsData.clubsPlural, order: 1 },
    { id: "speelplek", label: "Speelplek", order: 2 },
    { id: "wandelroute", label: "Wandelroute", order: 3 },
    { id: "fietsroute", label: "Fietsroute", order: 4 },
];

const playTypeFacets = computed<Facet[]>(() => {
    const hasActiveClubFilters = Object.values(clubFilterValues).some((values) => (values?.length ?? 0) > 0);

    const counts = props.pins.reduce<Record<string, number>>((acc, pin) => {
        if (hasActiveClubFilters && pin.type !== "aanbieder") {
            return acc;
        }

        acc[pin.type] = (acc[pin.type] ?? 0) + 1;
        return acc;
    }, {});

    return playTypeDefinitions.map((item) => ({
        Label: item.label,
        Count: counts[item.id] ?? 0,
        Id: item.id,
        Order: item.order,
    }));
});

const playTypeKenmerken = computed<Kenmerk[]>(() => {
    return [
        { Id: "aanbieder", Naam: "aanbieder", Beschrijving: "", Categorie: 0, Order: 1, Icon: "icon-home" },
        { Id: "speelplek", Naam: "speelplek", Beschrijving: "", Categorie: 0, Order: 2, Icon: "icon-playground" },
        { Id: "wandelroute", Naam: "wandelroute", Beschrijving: "", Categorie: 0, Order: 3, Icon: "icon-hiking" },
        { Id: "fietsroute", Naam: "fietsroute", Beschrijving: "", Categorie: 0, Order: 4, Icon: "icon-bike" },
    ];
});

const activityFacetDefinitions = computed(() => {
    const hidden = hiddenFiltersActivities.value;

    return [
        { key: "golfstarttype_facet", name: "Golfstart producten", buttonName: "golfstart producten", hide: !platformStore.isGolfPlatform },
        { key: "cultuuractiviteiten_facet", name: "Activiteit", buttonName: "activiteiten", hide: hidden.hideCultureActivityFilter },
        { key: "soortactiviteit_facet", name: "Soort activiteit", buttonName: "soort-activiteit", hide: hidden.hideMoveFilter },
        { key: "sporten_facet", name: "Sport", buttonName: "sporten", hide: hidden.hideSportFilter },
        { key: "sporttypen_facet", name: "Sporttype", buttonName: "sporttypen", hide: hidden.hideSportTypeFilter },
        { key: "plaatsen_facet", name: "Plaats", buttonName: "plaatsen", hide: hidden.hideLocationFilter },
        { key: "wijken_facet", name: "Wijk", buttonName: "wijken", hide: hidden.hideWijkFilter },
        { key: "wijkengemeenten_facet", name: "Wijk/Gemeente", buttonName: "wijken en gemeenten", hide: hidden.hideWijkGemeenteFilter },
        { key: "betalingstype_facet", name: "Betaling", buttonName: "betalingstypen", hide: hidden.hidePaymentTypeFilter },
        { key: "extrakosten_facet", name: "Extra kosten", buttonName: "extra kosten", hide: hidden.hideExtraCostsFilter },
        { key: "doelgroep_facet", name: "Doelgroep", buttonName: "doelgroepen", hide: hidden.hideTargetAudienceFilter },
        { key: "specifiekaanbod_facet", name: "Aangepast aanbod", buttonName: "aangepast aanbod", hide: hidden.hideAdeptedSportsFilter },
        { key: "fysiekeinspanning_facet", name: "Fysieke inspanning", buttonName: "inspanning", hide: hidden.hideEffortFilter },
    ];
});

const clubFacetDefinitions = computed(() => {
    const hidden = hiddenFiltersClubs.value;

    return [
        { key: "cultuuractiviteiten_facet", name: "Activiteit", buttonName: "activiteiten", hide: hidden.hideCultureActivityFilter },
        { key: "sporten_facet", name: "Sport", buttonName: "sporten", hide: hidden.hideSportFilter },
        { key: "plaatsen_facet", name: "Plaats", buttonName: "plaatsen", hide: hidden.hideLocationFilter },
        { key: "faciliteiten_facet", name: "Faciliteiten", buttonName: "faciliteiten", hide: hidden.hideFacilitiesFilter },
        { key: "aanvullendefaciliteiten_facet", name: "Aanvullende faciliteiten", buttonName: "aanvullende faciliteiten", hide: hidden.hideExtraFacilitiesFilter },
        { key: "specifiekaanbod_facet", name: "Aangepaste sporten", buttonName: "aangepaste sporten", hide: hidden.hideAdeptedSportsFilter },
        { key: "lesvormen_facet", name: "Lesvormen", buttonName: "lesvormen", hide: hidden.hideTeachingMethodsFilter },
    ];
});

const getFacetGroup = (groups: FacetGroup[], key: string): Facet[] => {
    return groups.find((group) => group.GroupName === key)?.Facets ?? [];
};

const getActivityFacetValues = (key: string): string[] => {
    const values = activityFilterValues[key as keyof IActiviteitSearchParams];
    return values ?? [];
};

const getClubFacetValues = (key: string): string[] => {
    const values = clubFilterValues[key as keyof ISportaanbiederSearchParams];
    return values ?? [];
};

const onActivityChange = (activityType: ActivityType): void => {
    selectedActivity.value = activityType;
};

const onToggleSidebar = (): void => {
    isSidebarCollapsed.value = !isSidebarCollapsed.value;
};

const activeFilterCount = computed(() => {
    if (selectedActivity.value === "spelen") {
        const clubFilters = Object.values(clubFilterValues).filter((v) => v && v.length > 0).length;
        const playTypeFilter = selectedPlayTypes.value.length > 0 ? 1 : 0;
        return clubFilters + playTypeFilter;
    }
    const activityFilters = Object.values(activityFilterValues).filter((v) => v && v.length > 0).length;
    const ageFilter = selectedAge.value !== undefined ? 1 : 0;
    return activityFilters + ageFilter;
});

const resetFilters = (): void => {
    for (const key of Object.keys(activityFilterValues)) {
        activityFilterValues[key as keyof IActiviteitSearchParams] = [];
    }
    for (const key of Object.keys(clubFilterValues)) {
        clubFilterValues[key as keyof ISportaanbiederSearchParams] = [];
    }
    selectedPlayTypes.value = [];
    selectedAge.value = undefined;
    emits("change-play-types", []);

    if (selectedActivity.value === "spelen") {
        emits("change-club-filters", {});
    } else {
        emits("change-activity-filters", {});
    }
};

const onMediaQueryChange = (e: MediaQueryListEvent): void => {
    isMobile.value = e.matches;
    if (!e.matches) {
        isSheetOpen.value = false;
    }
};

const onPlayTypeFacetChange = (_facetName: string, selectedFacets: string[] = []): void => {
    selectedPlayTypes.value = selectedFacets;
    emits("change-play-types", selectedFacets);
};

const onActivityFacetChange = (facetName: string, selectedFacets: string[] = []): void => {
    const typedFacetName = facetName as keyof IActiviteitSearchParams;
    activityFilterValues[typedFacetName] = selectedFacets;
    activityLastFacetChanged.value = selectedFacets.length > 0 ? facetName : undefined;
    const active: Record<string, string[] | number> = {};
    for (const [key, values] of Object.entries(activityFilterValues)) {
        if (values && values.length > 0) active[key] = values;
    }
    if (selectedAge.value !== undefined) {
        active.minLeeftijd = selectedAge.value;
        active.maxLeeftijd = selectedAge.value;
    }
    emits("change-activity-filters", active);
};

const onAgeChange = (): void => {
    if (selectedAge.value === undefined || Number.isNaN(selectedAge.value)) {
        selectedAge.value = undefined;
    } else {
        selectedAge.value = Math.min(Math.max(selectedAge.value, 0), 110);
    }

    const active: Record<string, string[] | number> = {};
    for (const [key, values] of Object.entries(activityFilterValues)) {
        if (values && values.length > 0) active[key] = values;
    }

    if (selectedAge.value !== undefined) {
        active.minLeeftijd = selectedAge.value;
        active.maxLeeftijd = selectedAge.value;
    }

    emits("change-activity-filters", active);
};

const onClubFacetChange = (facetName: string, selectedFacets: string[] = []): void => {
    const typedFacetName = facetName as keyof ISportaanbiederSearchParams;
    clubFilterValues[typedFacetName] = selectedFacets;
    clubLastFacetChanged.value = selectedFacets.length > 0 ? facetName : undefined;
    const active: Record<string, string[]> = {};
    for (const [key, values] of Object.entries(clubFilterValues)) {
        if (values && values.length > 0) active[key] = values;
    }
    emits("change-club-filters", active);
};

const loadKenmerken = async (): Promise<void> => {
    const [
        doelgroep,
        specifiekAanbod,
        fysiekeInspanning,
        golfstart,
        faciliteiten,
        aanvullendeFaciliteiten,
        lesvormen,
    ] = await Promise.all([
        kenmerkenRepository.getKenmerkenByCategorie(5),
        kenmerkenRepository.getKenmerkenByCategorie(2),
        kenmerkenRepository.getKenmerkenByCategorie(3),
        kenmerkenRepository.getKenmerkenByCategorie(29),
        kenmerkenRepository.getKenmerkenByCategorie(4),
        kenmerkenRepository.getKenmerkenByCategorie(9),
        kenmerkenRepository.getKenmerkenByCategorie(39),
    ]);

    activiteitKenmerken.doelgroep_facet = doelgroep;
    activiteitKenmerken.specifiekaanbod_facet = specifiekAanbod;
    activiteitKenmerken.fysiekeinspanning_facet = fysiekeInspanning;
    activiteitKenmerken.golfstarttype_facet = golfstart;

    clubKenmerken.specifiekaanbod_facet = specifiekAanbod;
    clubKenmerken.faciliteiten_facet = faciliteiten;
    clubKenmerken.aanvullendefaciliteiten_facet = aanvullendeFaciliteiten;
    clubKenmerken.lesvormen_facet = lesvormen;
};

watch(() => props.active, (newValue) => {
    if (newValue && selectedActivity.value !== newValue) {
        selectedActivity.value = newValue;
    }
});

watch(selectedActivity, (newValue) => {
    emits("change-activity", newValue);
    activityLastFacetChanged.value = undefined;
    clubLastFacetChanged.value = undefined;
});

onBeforeMount(async () => {
    await platformStore.ensurePlatform();
    await loadKenmerken();
});

onMounted(() => {
    mediaQueryList = window.matchMedia("(max-width: 991px)");
    isMobile.value = mediaQueryList.matches;
    mediaQueryList.addEventListener("change", onMediaQueryChange);
});

onUnmounted(() => {
    mediaQueryList?.removeEventListener("change", onMediaQueryChange);
});
</script>

<template>
    <!-- Mobile: trigger bar + bottom sheet -->
    <template v-if="isMobile">
        <div class="interactieve-kaart__filter-trigger">
            <span class="interactieve-kaart__filter-trigger-count">{{ resultCount }} locaties gevonden</span>
            <button class="interactieve-kaart__filter-trigger-btn" type="button" @click="isSheetOpen = true">
                <span class="icon icon-sliders"></span>
                <span v-if="activeFilterCount > 0" class="interactieve-kaart__filter-trigger-badge">{{ activeFilterCount }}</span>
            </button>
        </div>

        <Transition name="filter-sheet">
            <div v-if="isSheetOpen" class="interactieve-kaart__filter-sheet">
                <div class="interactieve-kaart__filter-sheet-header">
                    <div class="interactieve-kaart__filter-sheet-title">
                        <span class="icon icon-sliders"></span>
                        <h2 class="h5 mb-0">Filters</h2>
                        <span class="interactieve-kaart__filter-sheet-count">{{ resultCount }} locaties gevonden</span>
                    </div>
                    <button class="interactieve-kaart__filter-sheet-close" type="button" aria-label="Sluit filters"
                        @click="isSheetOpen = false">
                        <span class="icon icon-close"></span>
                    </button>
                </div>

                <div class="interactieve-kaart__filter-sheet-actions">
                    <button class="btn btn-reset" type="button" @click="resetFilters">
                        <span class="icon icon-reset_settings"></span>
                        Reset filters
                    </button>
                    <button class="btn btn-secondary btn-sm" type="button" @click="isSheetOpen = false">
                        Toon resultaten
                    </button>
                </div>

                <div class="interactieve-kaart__filter-sheet-content">
                    <div class="ic-filter">
                        <p class="ic-filter-title">Ik wil</p>
                        <InteractieveKaartActivitySwapper :active="selectedActivity" @change="onActivityChange" />
                    </div>

                    <template v-if="selectedActivity !== 'spelen'">
                        <div v-if="!hiddenFiltersActivities.hideAgeFilter" class="ic-filter">
                            <span class="ic-filter-title h4 gray-color">Leeftijd</span>
                            <div class="form-group mb-0">
                                <input type="number" min="0" max="110" placeholder="Leeftijd" name="leeftijdFilter"
                                    v-model.number="selectedAge" class="form-control" @input="onAgeChange" />
                            </div>
                        </div>

                        <FacetFilter v-for="filter in activityFacetDefinitions" v-show="!filter.hide" :key="filter.key"
                            :facet-name="filter.key" :facets="getFacetGroup(activityFacetGroups, filter.key)"
                            :active-facets="getActivityFacetValues(filter.key)" :name="filter.name"
                            :button-name="filter.buttonName"
                            :kenmerken="getFacetGroup(activityFacetGroups, filter.key).length > 0 ? activiteitKenmerken[filter.key] : undefined"
                            :last-facet-changed="activityLastFacetChanged" @change="onActivityFacetChange" />
                    </template>

                    <template v-else>
                        <FacetFilter facet-name="kaarttype_facet" :facets="playTypeFacets" :active-facets="selectedPlayTypes"
                            name="Type" button-name="typen" :kenmerken="playTypeKenmerken" @change="onPlayTypeFacetChange" />

                        <FacetFilter v-for="filter in clubFacetDefinitions" v-show="!filter.hide" :key="filter.key"
                            :facet-name="filter.key" :facets="getFacetGroup(clubFacetGroups, filter.key)"
                            :active-facets="getClubFacetValues(filter.key)" :name="filter.name"
                            :button-name="filter.buttonName"
                            :kenmerken="getFacetGroup(clubFacetGroups, filter.key).length > 0 ? clubKenmerken[filter.key] : undefined"
                            :last-facet-changed="clubLastFacetChanged" @change="onClubFacetChange" />
                    </template>
                </div>
            </div>
        </Transition>

        <Transition name="filter-backdrop">
            <div v-if="isSheetOpen" class="interactieve-kaart__filter-backdrop" @click="isSheetOpen = false"></div>
        </Transition>
    </template>

    <!-- Desktop: left sidebar panel -->
    <aside v-else class="interactieve-kaart__sidebar" :class="{ 'is-collapsed': isSidebarCollapsed }">
        <div class="interactieve-kaart__sidebar-header">
            <div class="interactieve-kaart__sidebar-title">
                <span class="icon icon-sliders"></span>
                <h2 class="h5 mb-0">Filters</h2>
                <button class="interactieve-kaart__sidebar-toggle" type="button" :aria-expanded="!isSidebarCollapsed"
                    aria-label="Toon of verberg filters" @click="onToggleSidebar">
                    <span class="icon icon-chevron-down" :class="{ 'is-rotated': !isSidebarCollapsed }"></span>
                </button>
            </div>
            <div class="interactieve-kaart__sidebar-divider"></div>
            <p class="interactieve-kaart__sidebar-description">{{ resultCount }} locaties gevonden</p>
        </div>

        <div v-show="!isSidebarCollapsed" class="interactieve-kaart__sidebar-content">
            <div class="ic-filter">
                <p class="ic-filter-title">Ik wil</p>
                <InteractieveKaartActivitySwapper :active="selectedActivity" @change="onActivityChange" />
            </div>

            <div class="interactieve-kaart__sidebar-divider"></div>

            <template v-if="selectedActivity !== 'spelen'">

                <div v-if="!hiddenFiltersActivities.hideAgeFilter" class="ic-filter">
                    <span class="ic-filter-title h4 gray-color">Leeftijd</span>
                    <div class="form-group mb-0">
                        <input type="number" min="0" max="110" placeholder="Leeftijd" name="leeftijdFilter"
                            v-model.number="selectedAge" class="form-control" @input="onAgeChange" />
                    </div>
                </div>

                <FacetFilter v-for="filter in activityFacetDefinitions" v-show="!filter.hide" :key="filter.key"
                    :facet-name="filter.key" :facets="getFacetGroup(activityFacetGroups, filter.key)"
                    :active-facets="getActivityFacetValues(filter.key)" :name="filter.name"
                    :button-name="filter.buttonName"
                    :kenmerken="getFacetGroup(activityFacetGroups, filter.key).length > 0 ? activiteitKenmerken[filter.key] : undefined"
                    :last-facet-changed="activityLastFacetChanged" @change="onActivityFacetChange" />
            </template>

            <template v-else>
                <FacetFilter facet-name="kaarttype_facet" :facets="playTypeFacets" :active-facets="selectedPlayTypes"
                    name="Type" button-name="typen" :kenmerken="playTypeKenmerken" @change="onPlayTypeFacetChange" />

                <FacetFilter v-for="filter in clubFacetDefinitions" v-show="!filter.hide" :key="filter.key"
                    :facet-name="filter.key" :facets="getFacetGroup(clubFacetGroups, filter.key)"
                    :active-facets="getClubFacetValues(filter.key)" :name="filter.name" :button-name="filter.buttonName"
                    :kenmerken="getFacetGroup(clubFacetGroups, filter.key).length > 0 ? clubKenmerken[filter.key] : undefined"
                    :last-facet-changed="clubLastFacetChanged"
                    @change="onClubFacetChange" />
            </template>
        </div>
    </aside>
</template>